import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { interactionStatusRpmOptions } from 'constants/forms';
import { languagesDataSource } from 'infrastructure/data-sources/languages';
import { timezonesDataSource } from 'infrastructure/data-sources/timezones';
import { readingTypesDataSource } from 'infrastructure/data-sources/reading-types';
import { statesDataSource } from 'infrastructure/data-sources/states';
import { overviewDashboardDataSource } from 'infrastructure/data-sources/overview-dashboard-interaction-time';
import { parentClinicsDataSource } from 'infrastructure/data-sources/parent-clinics';
import { ReadingStatus } from 'infrastructure/enums';

import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';

interface UseFilterLabelProps {
  patientName?: string | null;
  clinicName?: string | null;
  agencyDoctorName?: string | null;
}

export const useFilterLabel = ({
  patientName,
  clinicName,
  agencyDoctorName,
}: UseFilterLabelProps) => {
  const { t } = useTranslation();

  const getFilterLabel = useCallback(
    (key: keyof TOverviewDashboardFilter, value: any): string | null => {
      switch (key) {
        case 'status': {
          const statusLabels = {
            [ReadingStatus.Critical]: t('labels.critical', 'Critical'),
            [ReadingStatus.Risk]: t('labels.atRisk', 'At-Risk'),
            [ReadingStatus.Normal]: t('labels.normal', 'Normal'),
          };
          return `${t('labels.readingStatus', 'Reading Status')}: ${
            statusLabels[value as ReadingStatus]
          }`;
        }

        case 'reviewed':
          return `${t('labels.activitiesType', 'Activities Type')}: ${
            value
              ? t('labels.actioned', 'Actioned')
              : t('labels.unaction', 'Unaction')
          }`;

        case 'interactionStatus': {
          const statusOption = interactionStatusRpmOptions.find(
            (opt) => opt.value === value,
          );
          return statusOption
            ? `${t('labels.interactionStatus', 'Interaction Status')}: ${
                statusOption.label
              }`
            : null;
        }

        case 'interactiveTimeRange': {
          const timeOption = overviewDashboardDataSource().find(
            (opt) => opt.id === value,
          );
          return timeOption
            ? `${t('labels.interactiveTime', 'Interactive Time')}: ${
                timeOption.name
              }`
            : null;
        }

        case 'timezone': {
          const timezone = timezonesDataSource().find((tz) => tz.id === value);
          return timezone
            ? `${t('labels.patientTimezone', 'Patient Timezone')}: ${
                timezone.name
              }`
            : null;
        }

        case 'language': {
          const lang = languagesDataSource().find((l) => l.id === value);
          return lang
            ? `${t('labels.language', 'Language')}: ${lang.name}`
            : null;
        }

        case 'readingType': {
          const readingType = readingTypesDataSource().find(
            (rt: { id: any }) => rt.id === value,
          );
          return readingType
            ? `${t('labels.readingType', 'Reading Type')}: ${readingType.name}`
            : null;
        }

        case 'clinicState': {
          const state = statesDataSource().find((s) => s.id === value);
          return state
            ? `${t('labels.clinicState', 'Clinic State')}: ${state.name}`
            : null;
        }

        case 'patientGuid':
          return patientName
            ? `${t('labels.patient', 'Patient')}: ${patientName}`
            : null;

        case 'clinicGuid':
          return clinicName
            ? `${t('labels.clinic', 'Clinic')}: ${clinicName}`
            : null;

        case 'parentClinicOption': {
          const parentClinic = parentClinicsDataSource().find(
            (pc) => pc.id === value,
          );
          return parentClinic
            ? `${t('labels.parentClinic', 'Parent Clinic')}: ${
                parentClinic.name
              }`
            : null;
        }

        case 'actionedByAgencyDoctorGuid':
          return agencyDoctorName
            ? `${t(
                'labels.reviewedReadingBy',
                'Reviewed Reading By',
              )}: ${agencyDoctorName}`
            : null;

        default:
          return null;
      }
    },
    [t, patientName, clinicName, agencyDoctorName],
  );

  return { getFilterLabel };
};
